import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Leaf, Phone, MapPin, Mail, Instagram, Facebook } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Panchakarma" },
  { to: "/shop/medicines", label: "Medicines" },
  { to: "/shop/equipment", label: "Equipment" },
  { to: "/doctors", label: "Doctors" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  React.useEffect(() => setOpen(false), [location]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-md border-b"
      style={{ background: "rgba(250,248,245,0.88)", borderColor: "var(--ayu-border)" }}
    >
      <div className="px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span
            className="w-9 h-9 flex items-center justify-center"
            style={{ background: "var(--ayu-primary)", color: "var(--ayu-bg)" }}
          >
            <Leaf size={18} strokeWidth={1.5} />
          </span>
          <span className="font-display text-2xl tracking-tight" style={{ color: "var(--ayu-heading)" }}>
            Ayurveda
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-[var(--ayu-secondary)]" : "text-[var(--ayu-heading)] hover:text-[var(--ayu-secondary)]"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* Hide cart for admin user role */}
          {(!user || user.role !== "admin") && (
            <Link
              to="/cart"
              data-testid="cart-icon"
              className="relative p-2 hover:opacity-70 transition-opacity"
              aria-label="Cart"
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-1 -right-1 text-[10px] w-5 h-5 flex items-center justify-center font-medium"
                  style={{ background: "var(--ayu-secondary)", color: "var(--ayu-bg)" }}
                >
                  {count}
                </span>
              )}
            </Link>
          )}

          {/* User Auth Section */}
          <div className="h-6 w-[1px] bg-[var(--ayu-border)] hidden sm:block mx-1"></div>
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <Link to={user.role === 'admin' ? '/admin/orders' : '/profile'} className="flex items-center gap-2 hover:opacity-80">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover border border-[var(--ayu-border)]" />
                ) : (
                  <span className="w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold text-white bg-[var(--ayu-primary)]">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="hidden md:inline text-xs font-medium text-[var(--ayu-heading)]">
                  {user.role === 'admin' ? 'Admin' : user.name.split(' ')[0]}
                </span>
              </Link>
              <button onClick={logout} className="text-[10px] uppercase tracking-wider text-[var(--ayu-secondary)] hover:underline">
                Logout
              </button>
            </div>
          ) : (
            <Link to="/profile" className="text-sm font-medium hover:text-[var(--ayu-secondary)] transition-colors">
              Sign In
            </Link>
          )}

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t px-6 py-6 flex flex-col gap-4"
          style={{ background: "var(--ayu-bg)", borderColor: "var(--ayu-border)" }}
        >
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className="text-base"
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="mt-20"
    style={{ background: "var(--ayu-heading)", color: "var(--ayu-bg)" }}
  >
    <div className="px-6 md:px-12 lg:px-20 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
      <div className="md:col-span-1">
        <div className="flex items-center gap-2 mb-6">
          <span
            className="w-9 h-9 flex items-center justify-center"
            style={{ background: "var(--ayu-secondary)" }}
          >
            <Leaf size={18} strokeWidth={1.5} />
          </span>
          <span className="font-display text-2xl">Ayurveda</span>
        </div>
        <p className="text-sm opacity-70 leading-relaxed">
          A classical Ayurveda clinic, herbal apothecary, and Panchakarma sanctuary. Healing the
          way it has been done for five thousand years.
        </p>
      </div>

      <div>
        <p className="overline mb-6" style={{ color: "var(--ayu-accent)" }}>Explore</p>
        <ul className="space-y-3 text-sm">
          <li><Link to="/services">Panchakarma</Link></li>
          <li><Link to="/shop/medicines">Herbal Medicines</Link></li>
          <li><Link to="/shop/equipment">Clinical Equipment</Link></li>
          <li><Link to="/doctors">Our Doctors</Link></li>
          <li><Link to="/blog">Journal</Link></li>
        </ul>
      </div>

      <div>
        <p className="overline mb-6" style={{ color: "var(--ayu-accent)" }}>Visit Us</p>
        <ul className="space-y-3 text-sm opacity-80">
          <li className="flex gap-2 items-start"><MapPin size={14} className="mt-1 shrink-0" /> Ayurveda clinic, Near Shiv medico,<br />Siddhi terraces, lane no 18,<br />Raikar nagar, Dhayari, Pune</li>
          <li className="flex gap-2 items-center"><Phone size={14} /> +91 90110 43798</li>
          <li className="flex gap-2 items-center"><Mail size={14} /> care@ayurveda-pune-clinic.com</li>
        </ul>
      </div>

      <div>
        <p className="overline mb-6" style={{ color: "var(--ayu-accent)" }}>Newsletter</p>
        <p className="text-sm opacity-70 mb-4">Receive seasonal wisdom and apothecary updates.</p>
        <form
          onSubmit={(e) => e.preventDefault()}
          className="flex border-b"
          style={{ borderColor: "rgba(230,213,184,0.3)" }}
        >
          <input
            data-testid="newsletter-input"
            type="email"
            placeholder="Your email"
            className="bg-transparent flex-1 py-2 text-sm outline-none placeholder:opacity-50"
          />
          <button data-testid="newsletter-submit" className="text-sm px-3" style={{ color: "var(--ayu-accent)" }}>
            Subscribe
          </button>
        </form>
        <div className="flex gap-4 mt-8 opacity-80">
          <a href="#" aria-label="Instagram"><Instagram size={18} strokeWidth={1.5} /></a>
          <a href="#" aria-label="Facebook"><Facebook size={18} strokeWidth={1.5} /></a>
        </div>
      </div>
    </div>
    <div className="border-t px-6 md:px-12 lg:px-20 py-6 text-xs opacity-60 flex flex-col md:flex-row justify-between gap-2" style={{ borderColor: "rgba(230,213,184,0.15)" }}>
      <span>&copy; {new Date().getFullYear()} Ayurveda Clinic. All rights reserved.</span>
      <span>Crafted with reverence for tradition.</span>
    </div>
  </footer>
);

const Layout = () => (
  <div className="App">
    <Header />
    <main data-testid="main-content"><Outlet /></main>
    <Footer />
    <Toaster richColors position="top-center" />
  </div>
);

export default Layout;
